/**
 * Formatage français (§4 du contrat).
 *
 * Dates en `dd/MM/yyyy`, heures en 24 h, montants en euros avec **espace
 * insécable** avant le `€`. Ce dernier point n'est pas un caprice : une espace
 * ordinaire autorise le navigateur à couper la ligne entre le montant et le
 * symbole, et « 1 234,56 » sur une ligne avec « € » sur la suivante est
 * exactement le genre de détail qu'un client remarque en démonstration.
 */

import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'

/** Espace insécable U+00A0. Nommée, pour qu'on ne la confonde pas à la relecture. */
export const ESPACE_INSECABLE = ' '

/** "2026-03-02" → "02/03/2026". Accepte aussi un horodatage ISO complet. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '—'
  const [a, m, j] = date.slice(0, 10).split('-')
  if (!a || !m || !j) return '—'
  return `${j}/${m}/${a}`
}

/** "2026-03-02" → "lundi 2 mars 2026". */
export function formatDateLongue(date: string | null | undefined): string {
  if (!date) return '—'
  try {
    return format(parseISO(date.slice(0, 10)), 'EEEE d MMMM yyyy', { locale: fr })
  } catch {
    return formatDate(date)
  }
}

/**
 * "08:30" → "08:30". Tronque une entrée ISO et n'invente rien.
 *
 * Volontairement bête : les heures de planning sont **locales** et s'affichent
 * telles quelles. Les convertir serait le bug décrit au §5 des points durs.
 */
export function formatHeure(heure: string | null | undefined): string {
  if (!heure) return '—'
  return heure.slice(0, 5)
}

/**
 * Horodatage **UTC** → heure locale française.
 *
 * Réservé aux champs techniques (`created_at`, `heure_arrivee_reelle`), jamais
 * aux heures de planning.
 */
export function formatHorodatage(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return format(parseISO(iso), 'dd/MM/yyyy HH:mm', { locale: fr })
  } catch {
    return '—'
  }
}

/** 1234.56 → "1 234,56 €" avec espace insécable avant le symbole. */
export function formatMontant(montant: string | number | null | undefined): string {
  if (montant === null || montant === undefined || montant === '') return '—'
  const valeur = typeof montant === 'string' ? Number(montant) : montant
  if (!Number.isFinite(valeur)) return '—'
  const corps = valeur.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${corps}${ESPACE_INSECABLE}€`
}

/**
 * 105 → "1 h 45". Jamais "1.75 h" : le §4 impose des durées en minutes
 * entières, et un infirmier lit des heures et des minutes, pas des décimales.
 */
export function formatDuree(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—'
  const total = Math.max(0, Math.round(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h}${ESPACE_INSECABLE}h`
  return `${h}${ESPACE_INSECABLE}h${ESPACE_INSECABLE}${String(m).padStart(2, '0')}`
}

/** 2400 → "2,4 km" ; 850 → "850 m". Les distances sont des entiers de mètres. */
export function formatDistance(metres: number | null | undefined): string {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return '—'
  const m = Math.max(0, Math.round(metres))
  if (m < 1000) return `${m}${ESPACE_INSECABLE}m`
  return `${(m / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}${ESPACE_INSECABLE}km`
}

/** "Camille Berthier". */
export function nomComplet(p: { prenom: string; nom: string }): string {
  return `${p.prenom} ${p.nom}`
}

/** Initiales d'un infirmier — porteuses d'information là où la couleur ne suffit pas. */
export function initiales(p: { prenom: string; nom: string }): string {
  return `${p.prenom.charAt(0)}${p.nom.charAt(0)}`.toUpperCase()
}

/**
 * Énumération française : ["lundi","mercredi","vendredi"] → "lundi, mercredi et vendredi".
 *
 * Le « et » avant le dernier élément — pas une virgule. C'est ce qui distingue
 * une phrase d'une liste à puces mise bout à bout.
 */
export function enumerer(elements: readonly string[]): string {
  if (elements.length === 0) return ''
  if (elements.length === 1) return elements[0] ?? ''
  const debut = elements.slice(0, -1).join(', ')
  const dernier = elements[elements.length - 1] ?? ''
  return `${debut} et ${dernier}`
}
